import React, { useState, useEffect } from 'react';
import { HiOutlineRefresh } from 'react-icons/hi';
import { FiTrash } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TiStarFullOutline } from 'react-icons/ti';
import { useCart } from '../../../context/CartContext';
import { CheckoutPage } from '../../products/CheckoutPage';

import { FcCheckmark } from 'react-icons/fc';
import Modal from '../../../components/Modal';

export default function MyCart() {
  const { t } = useTranslation();
  const { cartItems, removeFromCart } = useCart();
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false); // 전체선택 상태 추가
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [orderName, setOrderName] = useState<string>('');

  useEffect(() => {
    // Initialize selected items to include all cart item IDs
    setSelectedItems(cartItems.map((item) => item.id));
    setIsAllSelected(true); // Set all selected state to true
  }, [cartItems]); // Only run when cartItems changes

  const handleProfileClick = (user: string) => {
    navigate(`/profile/${user}`);
  };

  const handleProductClick = (id: string) => {
    navigate(`/products/details/${id}`);
  };

  const handleCheckboxChange = (id: string) => {
    setSelectedItems((prevSelectedItems) => {
      const newSelectedItems = prevSelectedItems.includes(id)
        ? prevSelectedItems.filter((itemId) => itemId !== id)
        : [...prevSelectedItems, id];

      console.log('Updated selected items:', newSelectedItems);
      return newSelectedItems;
    });
  };

  // 전체선택 기능
  const handleSelectAll = () => {
    if (isAllSelected) {
      // 전체선택 해제
      setSelectedItems([]);
    } else {
      // 전체선택
      setSelectedItems(cartItems.map((item) => item.id));
    }
    setIsAllSelected(!isAllSelected);
  };

  const totalItems = selectedItems.length;
  const totalPrice = cartItems
    .filter((item) => selectedItems.includes(item.id))
    .reduce((acc, item) => acc + item.price, 0);

  const handlePurchase = () => {
    if (totalItems === 0) {
      console.log('No items selected for purchase.');
      return;
    }

    const selectedItemsDetails = cartItems.filter((item) =>
      selectedItems.includes(item.id)
    );
    const firstItemName = selectedItemsDetails[0]?.name;
    const additionalCount = selectedItemsDetails.length - 1;

    const newOrderName =
      additionalCount > 0
        ? `${firstItemName} 외 ${additionalCount}개`
        : firstItemName || '상품';

    setOrderName(newOrderName);
    console.log('Order name:', newOrderName);
    console.log('Navigating to checkout with data:', {
      productIds: selectedItems,
    });

    setIsModalOpen(true);
  };

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 767);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <section className="max-w-[2560px] w-full mx-auto mt-[130px] mb-[150px] mobile:mb-[80px] mobile:px-4 mobile:mt-[120px]">
      <div className="max-w-[1440px] w-full mx-auto">
        <h1 className="text-4xl Pretendard-Bold mb-2 mobile:text-xl mobile:mb-1">
          {t('cart.title')}
        </h1>
        <div className="flex items-center">
          <p className="text-gray-600 mr-[8px] Pretendard-Regular mobile:text-[11px]">
            {t('cart.checkAvailability')}
          </p>
          <HiOutlineRefresh />
        </div>

        <section className="flex pt-[24px] mobile:flex-col ">
          <div className="w-3/4 pb-[50px] box-border mobile:w-full">
            <div className="bg-slate-100 shadow-sm rounded-md p-3 text-left mb-4">
              <p className="text-JetBlack Pretendard-Regular text-sm mobile:text-xs">
                {t('cart.totalItems')} <em className="not-italic Pretendard-Bold">&nbsp;{cartItems.length}</em>개
              </p>
            </div>

            <article className="flex flex-col">
              {cartItems.length > 0 ? (
                <div className="bg-white border rounded-md p-4 shadow-md mobile:px-3">
                  <div className="border-b border-dashed pb-3">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        className="custom-checkbox form-checkbox cursor-pointer"
                        checked={isAllSelected} // 전체선택 상태
                        onChange={handleSelectAll} // 전체선택 이벤트 핸들러
                      />
                      <span className="font-medium text-sm leading-6">
                        {t('cart.totalCheck')}
                      </span>
                    </div>
                  </div>
                  {cartItems.map((item, index) => (
                    <>
                      {isMobile ? (
                        <div key={index} className="py-4">
                          <div className="flex items-center gap-4">
                            <div className="items-center cursor-pointer flex flex-row justify-center text-[13px] h-[90px] text-center leading-4 text-MainTextColor ">
                              <input
                                type="checkbox"
                                className="custom-checkbox form-checkbox cursor-pointer mt-7"
                                checked={selectedItems.includes(item.id)}
                                onChange={() => handleCheckboxChange(item.id)}
                              />
                            </div>
                            <div className="w-full">
                              <div className="flex items-center gap-1">
                                <img
                                  src={`${process.env.REACT_APP_API_BASE_URL}${item.profileImageUrl}`}
                                  alt={item.name}
                                  className="ml-[2px] mr-[4px] w-[16px] h-full object-cover cursor-pointer"
                                  onClick={() => handleProductClick(item.id)}
                                />
                                <p
                                  className="text-DarkAccent text-sm mobile:text-[11px] cursor-pointer overflow-hidden text-nowrap text-ellipsis w-[150px]"
                                  onClick={() => handleProfileClick(item.user)}
                                >
                                  {item.user}
                                </p>
                              </div>
                              <div className="flex justify-between">
                                <div className="flex items-center gap-4 mt-2">
                                  <div className="w-20 h-20 bg-gray-100 rounded overflow-hidden">
                                    <img
                                      src={`${process.env.REACT_APP_API_BASE_URL}${item.image}`}
                                      alt={item.name}
                                      className="w-full h-full object-cover cursor-pointer"
                                      onClick={() =>
                                        handleProductClick(item.id)
                                      }
                                    />
                                  </div>
                                  <div className="w-[150px]">
                                    <p className="text-black text-sm mobile:text-[10px] tracking-wide font-normal">
                                      {item.category}
                                    </p>
                                    <p
                                      className="text-black text-sm mobile:text-[13px] cursor-pointer tracking-wide font-semibold whitespace-nowrap overflow-hidden text-ellipsis"
                                      onClick={() =>
                                        handleProductClick(item.id)
                                      }
                                    >
                                      {item.name}
                                    </p>
                                    <div>
                                      <p className="font-medium text-sm mobile:text-xs">
                                        수량 : 1
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-DarkAccent text-sm mobile:text-xs font-semibold mr-2 ">
                                        {item.price.toLocaleString()}원
                                      </p>
                                    </div>
                                  </div>
                                </div>
                                <button
                                  className="text-gray-400 hover:text-red-500 pr-4"
                                  onClick={() => removeFromCart(item.id)}
                                >
                                  <FiTrash size={15} />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div
                          key={index}
                          className="flex items-center justify-between gap-4 mb-4 mt-6 rounded-md"
                        >
                          <div className="flex items-center gap-4">
                            <div className="items-center cursor-pointer flex flex-row justify-center text-[13px] h-[90px] text-center leading-4 text-MainTextColor ">
                              <input
                                type="checkbox"
                                className="custom-checkbox form-checkbox cursor-pointer"
                                checked={selectedItems.includes(item.id)}
                                onChange={() => handleCheckboxChange(item.id)}
                              />
                            </div>

                            <div className="w-20 h-20 mobile:w-[60px] mobile:h-[60px] bg-gray-100 rounded overflow-hidden">
                              <img
                                src={`${process.env.REACT_APP_API_BASE_URL}${item.image}`}
                                alt={item.name}
                                className="w-full h-full object-cover cursor-pointer"
                                onClick={() => handleProductClick(item.id)}
                              />
                            </div>

                            <div className="w-[150px]">
                              <p className="text-black text-sm tracking-wide Pretendard-Regular">
                                {item.category}
                              </p>
                              <p
                                className="text-black cursor-pointer tracking-wide Pretendard-Bold whitespace-nowrap overflow-hidden text-ellipsis"
                                onClick={() => handleProductClick(item.id)}
                              >
                                {item.name}
                              </p>
                              {/* <div className="flex items-center font-normal gap-1 mobile:gap-[2px] text-xs mobile:text-[10px] text-[#878f918a] ">
                  <TiStarFullOutline className="mb-[2px] mobile:mb-[0px]" />
                  <span className="text-[#878f91]">
                    {item.review}
                  </span>
                </div> */}
                            </div>
                          </div>

                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1">
                              <img
                                src={`${process.env.REACT_APP_API_BASE_URL}${item.profileImageUrl}`}
                                alt={item.name}
                                className="ml-[2px] mr-[4px] w-[16px] h-full object-cover cursor-pointer"
                                onClick={() => handleProductClick(item.id)}
                              />
                              <p
                                className="text-DarkAccent Pretendard-Regular text-sm cursor-pointer overflow-hidden text-nowrap text-ellipsis w-[150px]"
                                onClick={() => handleProfileClick(item.user)}
                              >
                                {item.user}
                                {item.user}
                              </p>
                            </div>
                            <div className="flex items-center">
                              <p className="Pretendard-Bold text-sm px-4 w-[45px] text-center">
                                1
                              </p>
                            </div>

                            <div className="flex items-center">
                              <p className="text-MainTextColor Pretendard-Bold mr-2 min-w-[80px] text-right">
                                {item.price.toLocaleString()}원
                              </p>
                            </div>

                            <button
                              className="text-gray-400 hover:text-red-500 pr-4"
                              onClick={() => removeFromCart(item.id)}
                            >
                              <FiTrash size={15} />
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  ))}
                </div>
              ) : (
                <p className="text-white">{t('cart.emptyCart')}</p>
              )}
            </article>
          </div>

          <div className="w-1/4 mobile:w-full ml-6 mobile:ml-0 bg-white border shadow-md max-h-[500px] rounded-2xl px-6 py-4">
            <div className="sticky">
              <h4 className="text-2xl mobile:text-xl pb-4 pt-2 Pretendard-Bold text-[#46433f]  border-dashed  border-b leading-8">
                {t('cart.pricing')}
              </h4>
                <div className="mt-4">
                  <span className="text-sm Pretendard-Regular text-DarkGray flex items-center gap-2">
                    <FcCheckmark />
                    {t('cart.amount')}
                  </span>
                  <p className="text-base Pretendard-Regular text-DarkGray mt-2">
                    {totalItems}
                    {t('cart.amountCount')}
                  </p>
                </div>

                <div className="mt-4">
                  <span className="text-sm Pretendard-Regular text-DarkGray flex items-center gap-2">
                    <FcCheckmark /> {t('cart.originalTotal')}
                  </span>
                  <p className="text-base Pretendard-Regular text-DarkGray mt-2">
                    {cartItems
                      .filter((item) => selectedItems.includes(item.id))
                      .reduce((acc, item) => acc + (item.originalPrice || 0), 0)
                      .toLocaleString()}
                    원
                  </p>
                </div>

                <div className="mt-4">
                  <span className="text-sm Pretendard-Regular text-DarkGray flex items-center gap-2">
                    <FcCheckmark /> {t('cart.discountTotal')}
                  </span>
                  <p className="text-base Pretendard-Regular text-DarkGray mt-2">
                    {cartItems
                      .filter((item) => selectedItems.includes(item.id))
                      .reduce(
                        (acc, item) =>
                          acc + ((item.originalPrice || 0) - (item.price || 0)),
                        0
                      )
                      .toLocaleString()}
                    원
                  </p>
                </div>
                <div className="mt-4 border-b border-dashed ">
                <div className="mb-4">
                  <span className="text-xl mobile:text-lg Pretendard-Bold text-DarkAccent">
                    {t('cart.total')}
                  </span>
                  <p className="text-xl mobile:text-lg  Pretendard-Bold text-DarkAccent mt-2">
                    {totalPrice.toLocaleString()}원
                  </p>
                </div>
              </div>

              <button
                className="w-full mt-6 shadow-md rounded-lg cursor-pointer tracking-wide bg-teal-500 hover:bg-teal-400 text-white text-lg Pretendard-Bold py-3 transition duration-300"
                disabled={totalItems === 0}
                onClick={handlePurchase}
              >
                {t('cart.buy')}
              </button>
            </div>
          </div>
        </section>
      </div>

      {isModalOpen && (
        <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
          <CheckoutPage
            initialAmount={{ currency: 'KRW', value: totalPrice }}
            orderName={orderName}
            productIds={selectedItems}
          />
        </Modal>
      )}
    </section>
  );
}
